import * as sdk from 'matrix-js-sdk'
import { decodeRecoveryKey } from 'matrix-js-sdk/lib/crypto-api/recovery-key'

let matrixClient: sdk.MatrixClient | null = null
let cryptoReady = false
let cachedRecoveryKey: Uint8Array | null = null

interface CreateClientOpts {
  baseUrl: string
  accessToken?: string
  userId?: string
  deviceId?: string
}

export async function createClient(opts: CreateClientOpts): Promise<sdk.MatrixClient> {
  if (matrixClient) {
    matrixClient.stopClient()
  }

  cryptoReady = false

  matrixClient = sdk.createClient({
    baseUrl: opts.baseUrl,
    accessToken: opts.accessToken,
    userId: opts.userId,
    deviceId: opts.deviceId,
    useAuthorizationHeader: true,
    timelineSupport: true,
    supportsCallTransfer: false,
    cryptoCallbacks: {
      getSecretStorageKey: async ({ keys }) => {
        if (!cachedRecoveryKey) {
          throw new Error('No recovery key available')
        }
        const keyId = Object.keys(keys ?? {})[0]
        if (!keyId) throw new Error('No key ID found')
        return [keyId, cachedRecoveryKey]
      },
    },
  })

  try {
    await matrixClient.initRustCrypto()
    cryptoReady = true
    console.log('Crypto initialized successfully')
  } catch (err) {
    if (String(err).includes("doesn't match")) {
      console.warn('Crypto store mismatch, clearing and retrying...')
      const dbs = await indexedDB.databases()
      for (const db of dbs) {
        if (db.name && (db.name.includes('matrix-sdk-crypto') || db.name.includes('rust-crypto'))) {
          indexedDB.deleteDatabase(db.name)
        }
      }
      try {
        await matrixClient.initRustCrypto()
        cryptoReady = true
        console.log('Crypto initialized after store reset')
      } catch (retryErr) {
        console.warn('Crypto init failed after reset:', retryErr)
      }
    } else {
      console.warn('Crypto init failed, E2EE unavailable:', err)
    }
  }

  return matrixClient
}

export function cacheRecoveryKey(recoveryKeyString: string): void {
  cachedRecoveryKey = decodeRecoveryKey(recoveryKeyString)
}

export async function ensureCrypto(): Promise<void> {
  if (cryptoReady) return
  if (!matrixClient) throw new Error('Matrix client not initialized')
  await matrixClient.initRustCrypto()
  cryptoReady = true
}

export function isCryptoReady(): boolean {
  return cryptoReady
}

export function getClient(): sdk.MatrixClient | null {
  return matrixClient
}

export function requireClient(): sdk.MatrixClient {
  if (!matrixClient) throw new Error('Matrix client not initialized')
  return matrixClient
}

export function destroyClient() {
  if (matrixClient) {
    matrixClient.stopClient()
    matrixClient = null
    cryptoReady = false
  }
}
